import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { lawyerInfoService } from "../services/lawyerInfoService";
import { RepresentativeInfo } from "../types/lawyer";
import LawyerInfoModal from "./LawyerInfoModal";
import "./LawyerInfoTab.css";

interface LawyerInfoTabProps {
  onProfileSaved?: () => void;
}

const LawyerInfoTab: React.FC<LawyerInfoTabProps> = ({ onProfileSaved }) => {
  const { user } = useAuth();
  const [lawyerInfo, setLawyerInfo] = useState<any>(null);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isRemovingAvatar, setIsRemovingAvatar] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadLawyerInfo();
    }
  }, [user?.id]);

  const loadLawyerInfo = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await lawyerInfoService.getLawyerInfo(user!.id);

      if (error) {
        console.error("Error loading lawyer info:", error);
        return;
      }

      setLawyerInfo(data);
      setIsProfileComplete(data?.profile_completed || false);
    } catch (error) {
      console.error("Error in loadLawyerInfo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: "avatar" | "logo"
  ) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    try {
      if (type === "avatar") {
        setIsUploadingAvatar(true);
        await lawyerInfoService.uploadProfileImage(file);
      } else {
        setIsUploadingLogo(true);
        await lawyerInfoService.uploadFirmLogo(file);
      }

      await loadLawyerInfo();
    } catch (error) {
      console.error("Error uploading branding image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      if (type === "avatar") {
        setIsUploadingAvatar(false);
      } else {
        setIsUploadingLogo(false);
      }
    }
  };

  const handleImageRemove = async (type: "avatar" | "logo") => {
    if (!user?.id) return;

    const confirmMessage = type === "avatar" 
      ? "Are you sure you want to remove your profile photo?" 
      : "Are you sure you want to remove your firm logo?";
    
    if (!window.confirm(confirmMessage)) return;

    try {
      if (type === "avatar") {
        setIsRemovingAvatar(true);
        await lawyerInfoService.deleteProfileImage();
      } else {
        setIsRemovingLogo(true);
        await lawyerInfoService.deleteFirmLogo();
      }

      await loadLawyerInfo();
    } catch (error) {
      console.error("Error removing branding image:", error);
      alert("Failed to remove image. Please try again.");
    } finally {
      if (type === "avatar") {
        setIsRemovingAvatar(false);
      } else {
        setIsRemovingLogo(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      await loadLawyerInfo();
      setShowModal(false);
      // Reload profile info to get the latest status
      await loadLawyerInfo();

      // Notify parent component that profile was saved
      // Add a delay to ensure backend has processed the update
      if (onProfileSaved) {
        // First check immediately
        onProfileSaved();
        // Then check again after a delay to ensure backend has processed
        setTimeout(() => {
          console.log("Re-checking profile after save...");
          onProfileSaved();
        }, 1500);
        // One more check after a longer delay
        setTimeout(() => {
          console.log("Final profile check after save...");
          onProfileSaved();
        }, 3000);
      }
    } catch (error) {
      console.error("Error in handleSave:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="lawyer-info-tab">
        <div className="loading">Loading profile information...</div>
      </div>
    );
  }

  return (
    <div className="lawyer-info-tab">
      {/* Header */}
      <div className="tab-header">
        <h2>Profile Information</h2>
        <button className="edit-btn" onClick={() => setShowModal(true)}>
          {isProfileComplete ? "Edit Info" : "Add Info"}
        </button>
      </div>

      {lawyerInfo?.representative_info && (
        <div className="branding-section">
          <div className="branding-avatar-block">
            <div className="branding-label">Profile Photo</div>
            <div className="branding-avatar-preview">
              {lawyerInfo.representative_info.profileImageUrl ? (
                <img
                  src={lawyerInfo.representative_info.profileImageUrl}
                  alt="Profile"
                  className="branding-avatar-image"
                />
              ) : (
                <div className="branding-avatar-placeholder">
                  {lawyerInfo.representative_info.firstName?.[0] || ""}
                  {lawyerInfo.representative_info.lastName?.[0] || ""}
                </div>
              )}
            </div>
            <div className="branding-button-group">
              <label className="branding-upload-button">
                {isUploadingAvatar ? "Uploading…" : "Upload Photo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "avatar")}
                  disabled={isUploadingAvatar || isRemovingAvatar}
                />
              </label>
              {lawyerInfo.representative_info.profileImageUrl && (
                <button
                  className="branding-remove-button"
                  onClick={() => handleImageRemove("avatar")}
                  disabled={isUploadingAvatar || isRemovingAvatar}
                >
                  {isRemovingAvatar ? "Removing…" : "Remove"}
                </button>
              )}
            </div>
          </div>

          <div className="branding-logo-block">
            <div className="branding-label">Firm Logo</div>
            <div className="branding-logo-preview">
              {lawyerInfo.representative_info.firmLogoUrl ? (
                <img
                  src={lawyerInfo.representative_info.firmLogoUrl}
                  alt="Firm logo"
                  className="branding-logo-image"
                />
              ) : (
                <div className="branding-logo-placeholder">
                  {lawyerInfo.representative_info.lawFirm || "Your Firm"}
                </div>
              )}
            </div>
            <div className="branding-button-group">
              <label className="branding-upload-button">
                {isUploadingLogo ? "Uploading…" : "Upload Logo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "logo")}
                  disabled={isUploadingLogo || isRemovingLogo}
                />
              </label>
              {lawyerInfo.representative_info.firmLogoUrl && (
                <button
                  className="branding-remove-button"
                  onClick={() => handleImageRemove("logo")}
                  disabled={isUploadingLogo || isRemovingLogo}
                >
                  {isRemovingLogo ? "Removing…" : "Remove"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tip Message */}
      <div
        className="tip-message"
        style={{
          backgroundColor: isProfileComplete ? "#d1fae5" : "#fff3cd",
          borderColor: isProfileComplete ? "#10b981" : "#ffc107",
        }}
      >
        <div className="tip-icon">{isProfileComplete ? "✅" : "⚠️"}</div>
        <div className="tip-content">
          {isProfileComplete ? (
            <>
              <strong>Profile Complete:</strong> Your profile information is
              complete. You will receive consultation summary notifications via
              email.
            </>
          ) : (
            <>
              <strong>Profile Setup Required:</strong> Please complete your
              profile information. Your email address is required to receive
              consultation summary notifications via email. Once complete,
              you'll have full access to the dashboard.
            </>
          )}
        </div>
      </div>

      {/* Lawyer Info Display */}
      {isProfileComplete && lawyerInfo?.representative_info ? (
        <div className="lawyer-info-display">
          <h3>Current Information</h3>
          <div className="info-grid">
            <div className="info-section">
              <h4>Personal Information</h4>
              <div className="info-item">
                <label>Name:</label>
                <span>
                  {lawyerInfo.representative_info.firstName}{" "}
                  {lawyerInfo.representative_info.lastName}
                </span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{lawyerInfo.representative_info.email}</span>
              </div>
              <div className="info-item">
                <label>Phone:</label>
                <span>
                  +{lawyerInfo.representative_info.phoneCountryCode}{" "}
                  {lawyerInfo.representative_info.phoneNumber}
                </span>
              </div>
              {lawyerInfo.representative_info.faxNumber && (
                <div className="info-item">
                  <label>Fax:</label>
                  <span>
                    +{lawyerInfo.representative_info.faxCountryCode}{" "}
                    {lawyerInfo.representative_info.faxNumber}
                  </span>
                </div>
              )}
            </div>

            <div className="info-section">
              <h4>Professional Information</h4>
              {lawyerInfo.industry && (
                <div className="info-item">
                  <label>Industry:</label>
                  <span style={{ textTransform: "capitalize" }}>
                    {lawyerInfo.industry === "real_estate"
                      ? "Real Estate"
                      : "Legal"}
                  </span>
                </div>
              )}
              <div className="info-item">
                <label>Firm:</label>
                <span>{lawyerInfo.representative_info.lawFirm}</span>
              </div>
            </div>

            <div className="info-section">
              <h4>Address</h4>
              <div className="info-item">
                <label>Street Address:</label>
                <span>
                  {lawyerInfo.representative_info.address.streetNumber}{" "}
                  {lawyerInfo.representative_info.address.streetName}
                  {lawyerInfo.representative_info.address.aptNumber &&
                    `, Apt ${lawyerInfo.representative_info.address.aptNumber}`}
                </span>
              </div>
              <div className="info-item">
                <label>City/Province:</label>
                <span>
                  {lawyerInfo.representative_info.address.city},{" "}
                  {lawyerInfo.representative_info.address.province}
                </span>
              </div>
              <div className="info-item">
                <label>Postal Code:</label>
                <span>{lawyerInfo.representative_info.address.postalCode}</span>
              </div>
              <div className="info-item">
                <label>Country:</label>
                <span>{lawyerInfo.representative_info.address.country}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">👨‍💼</div>
          <h3>No Profile Information Added</h3>
          <p>
            Add your profile information to ensure all forms include your
            professional details for client submissions.
          </p>
          <button className="add-info-btn" onClick={() => setShowModal(true)}>
            Add Profile Information
          </button>
        </div>
      )}

      {/* Lawyer Info Modal */}
      {showModal && (
        <LawyerInfoModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          existingData={lawyerInfo?.representative_info}
          existingIndustry={lawyerInfo?.industry} // Pass industry from top-level lawyerInfo
        />
      )}
    </div>
  );
};

export default LawyerInfoTab;
